import React, { useEffect, useState } from "react";
import { getLoggedInUser } from "../helpers/fakebackend_helper";
import { useNavigate, useLocation } from "react-router-dom";
import { roleMenuConfig } from "./roleMenuConfig";

const Navdata = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user profile and role
  const userProfile = getLoggedInUser();
  const userRoles =
    userProfile?.associatedRoles ||
    userProfile?.roles ||
    (userProfile?.role ? [userProfile?.role] : []);
  const userRole = userRoles?.[0];

  // Get role config
  const roleConfig = userRole ? roleMenuConfig[userRole] : null;
  const hasFullAccess = roleConfig?.accessAll || false;
  const allowedMenuItems = roleConfig?.menuItems || [];
  const defaultRedirect = roleConfig?.defaultRedirect || null;

  // Single state for tracking active menu
  const [activeMenu, setActiveMenu] = useState(null);

  // Redirect to default path based on role
  useEffect(() => {
    if (defaultRedirect && location.pathname === "/dashboard") {
      navigate(defaultRedirect, { replace: true });
    }
  }, [defaultRedirect, navigate, location.pathname]);

  // Update active menu based on current path
  useEffect(() => {
    const path = location.pathname;

    // Determine which menu should be active based on path
    if (path.includes("/ads")) {
      setActiveMenu("ads");
    } else if (path.includes("/user-mgmt")) {
      setActiveMenu("user-management");
    } else if (path.includes("/db")) {
      setActiveMenu("database-management");
    } else if (path.includes("/projects")) {
      setActiveMenu("project-management");
    } else if (path.includes("/advertiser")) {
      setActiveMenu("advertisers");
    } else if (path.includes("/campaign")) {
      setActiveMenu("campaigns");
    } else if (path.includes("/logs")) {
      setActiveMenu("logs");
    } else if (
      path.includes("/device-heartbeat") ||
      path.includes("/device-viewer")
    ) {
      setActiveMenu("device-heartbeat");
    } else {
      setActiveMenu("Dashboard");
    }
  }, [location.pathname]);

  // Helper function to handle menu click
  const handleMenuClick = (menuId, e) => {
    e.preventDefault();
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  const closeMenu = (e) => {
    e.preventDefault();
  };

  // Base menu structure - define once, filter by role
  const baseMenuItems = [
    {
      label: "Menu",
      isHeader: true,
    },
    {
      id: "Dashboard",
      label: "Dashboard",
      icon: "ri-dashboard-line",
      link: "/dashboard",
      click: (e) => handleMenuClick("Dashboard", e),
    },
    {
      id: "user-management",
      label: "User management",
      icon: "ri-group-line",
      link: "/user-mgmt",
      click: (e) => handleMenuClick("user-management", e),
    },
    {
      id: "database-management",
      label: "Database management",
      icon: "ri-database-2-line",
      link: "/db",
      click: (e) => handleMenuClick("database-management", e),
    },
    {
      id: "project-management",
      label: "Project Management",
      icon: "ri-task-line",
      link: "/projects",
      click: (e) => handleMenuClick("project-management", e),
    },
    {
      id: "cloud-restro-users",
      label: "Cloud Restro Users",
      icon: "ri-account-circle-line",
      link: "/cloudrestrousers",
      click: (e) => closeMenu(e),
    },
    {
      id: "organization-devices",
      label: "Organization Devices",
      icon: "ri-building-2-line",
      link: "/organization-devices",
      click: (e) => closeMenu(e),
    },
    {
      id: "restro-user-info",
      label: "Restro Users Info",
      icon: "ri-database-2-line",
      link: "/restrousers",
      click: (e) => closeMenu(e),
    },

    {
      id: "ads-daily-stats",
      label: "Ads Daily Stats",
      link: "/ads/daily-stats",
      icon: "ri-bar-chart-line", // Shows stats
      click: (e) => closeMenu(e),
    },

    //Device Management
    {
      id: "device-heartbeat",
      label: "Device Management",
      icon: "ri-server-line", // server/device icon fits
      link: "#",
      click: (e) => handleMenuClick("device-heartbeat", e),
      parentId: "device-heartbeat",
      isExpanded: activeMenu === "device-heartbeat",
      subItems: [
        {
          id: "multi-ventor",
          label: "Multi Vendor",
          link: "/organization-devices",
          parentId: "device-heartbeat",
        },
        {
          id: "organization-list",
          label: "Organization List",
          link: "/organization-list",
          parentId: "device-heartbeat",
        },
        {
          id: "device-register",
          label: "Device Registration",
          link: "/device/new",
          parentId: "device-heartbeat",
        },
        {
          id: "device-heartbeat-status",
          label: "Device Status",
          link: "/device-heartbeat-status",
          parentId: "device-heartbeat",
        },
        {
          id: "device-logs",
          label: "Device Logs",
          link: "/device-logs",
          parentId: "device-heartbeat",
        },

        // {
        //   id: "device-viewer",
        //   label: "Device Viewer",
        //   link: "/device-viewer",
        //   parentId: "device-heartbeat",
        // },
      ],
    },

    //Advertisers
    {
      id: "advertisers",
      label: "Advertisers",
      icon: "ri-building-line", // company / advertiser
      link: "#",
      click: (e) => handleMenuClick("advertisers", e),
      parentId: "advertisers",
      isExpanded: activeMenu === "advertisers",
      subItems: [
        {
          id: "advertiser-list",
          label: "Advertiser List",
          link: "/advertiser-list",
          parentId: "advertisers",
        },
        {
          id: "advertiser-stats",
          label: "Advertiser Stats",
          link: "/advertiser-stats",
          parentId: "advertisers",
        },
      ],
    },

    {
      id: "ads-post",
      label: "Media Library",
      link: "/ads",
      icon: "ri-gallery-line", // Better icon for media/photos
      click: (e) => closeMenu(e),
    },

    //Campaigns
    {
      id: "campaigns",
      label: "Campaigns",
      icon: "ri-flag-line", // flag for campaigns
      link: "#",
      click: (e) => handleMenuClick("campaigns", e),
      parentId: "campaigns",
      isExpanded: activeMenu === "campaigns",
      subItems: [
        // {
        //   id: "assign-campaign",
        //   label: "Assign Campaign",
        //   link: "/assign-campaign",
        //   parentId: "campaigns",
        // },
        {
          id: "campaign-list",
          label: "Campaign List",
          link: "/campaign",
          parentId: "campaigns",
        },
        {
          id: "campaign-stat",
          label: "Campaign Stats",
          link: "/campaign-stats",
          parentId: "campaigns",
        },
        {
          id: "campaign-schedule",
          label: "Campaign Schedule",
          link: "/campaign-schedule",
          parentId: "campaigns",
        },
        {
          id: "campaign-calendar",
          label: "Campaign Calendar",
          link: "/campaign-calendar",
          parentId: "campaigns",
        },
      ],
    },

    // {
    //   id: "ads",
    //   label: "Ads Mgmt",
    //   icon: "bx bxs-report",
    //   link: "#",
    //   click: (e) => handleMenuClick("ads", e),
    //   parentId: "ads",
    //   isExpanded: activeMenu === "ads",
    //   subItems: [
    //     // {
    //     //   id: "ads-daily-stats",
    //     //   label: "Ads Daily Stats",
    //     //   link: "/ads/daily-stats",
    //     //   parentId: "ads",
    //     // },
    //     {
    //       id: "ads-post",
    //       label: "Media Library",
    //       link: "/ads",
    //       parentId: "ads",
    //     },
    //     // {
    //     //   id: "ads-logs",
    //     //   label: "Ads Logs",
    //     //   link: "/ads/logs",
    //     //   parentId: "ads",
    //     // },
    //     // {
    //     //   id: "kiosk-active-ads",
    //     //   label: "Kiosk Active Ads",
    //     //   link: "/ads/device-ads",
    //     //   parentId: "ads",
    //     // },
    //     // {
    //     //   id: "kiosk-ads-manager",
    //     //   label: "Kiosk Ads Manager",
    //     //   link: "/ads/kiosk-manager",
    //     //   parentId: "ads",
    //     // },
    //   ],
    // },

    //Ads Logs
    {
      id: "ads-logs",
      label: "Ads Logs",
      link: "/ads/logs",
      icon: "ri-file-list-line", // Logs/list
      click: (e) => closeMenu(e),
    },

    // {
    //   id: "assign-media",
    //   label: "Assign Media",
    //   icon: "ri-server-line",
    //   link: "#",
    //   click: (e) => handleMenuClick("assign-media", e),
    //   parentId: "assign-media",
    //   isExpanded: activeMenu === "assign-media",
    //   subItems: [
    //     {
    //       id: "assign-media-list",
    //       label: "Assign Media",
    //       link: "/assign-media",
    //       parentId: "assign-media",
    //     },

    //     {
    //       id: "unassign-media-list",
    //       label: "Unassign Media",
    //       link: "/unassign-media",
    //       parentId: "assign-media",
    //     },
    //   ],
    // },
    {
      id: "logs",
      label: "Logs",
      icon: "bx bxs-report",
      link: "#",
      click: (e) => handleMenuClick("logs", e),
      parentId: "logs",
      isExpanded: activeMenu === "logs",
      subItems: [
        {
          id: "restro-users-logs",
          label: "Restro Users Logs",
          link: "/restrouserslogs",
          parentId: "logs",
        },
        {
          id: "restro-users-query-log",
          label: "Restro Users Query Log",
          link: "/restrousersquerylogs",
          parentId: "logs",
        },
      ],
    },
    {
      id: "setting",
      label: "Settings",
      icon: "ri-settings-3-line",
      link: "#",
      click: (e) => handleMenuClick("setting", e),
      parentId: "setting",
    },
  ];

  // Filter menu items based on role
  const getFilteredMenuItems = () => {
    if (hasFullAccess) {
      return baseMenuItems;
    }

    if (allowedMenuItems.length === 0) {
      return [];
    }

    return baseMenuItems.filter((item) => {
      return item.isHeader || allowedMenuItems.includes(item.id);
    });
  };

  const filteredMenuItems = getFilteredMenuItems();

  return <React.Fragment>{filteredMenuItems}</React.Fragment>;
};

export default Navdata;
